import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Mail, Send, CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/lib/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) { toast.error("Bitte füllen Sie alle Pflichtfelder aus"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, subject, message });
      setSent(true);
      toast.success(t("contact_success_title"));
    } catch (err) {
      const errMsg = err.response?.data?.detail?.[0]?.msg?.replace("Value error, ", "") || "Error";
      toast.error(errMsg);
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#FAFAFA]">
      <Helmet><title>{`${t("contact_title")} | Oona Flats Wien`}</title></Helmet>
      <Navbar />
      <div className="pt-24 md:pt-28 pb-20 md:pb-24 px-5 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="mb-10 md:mb-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mx-auto mb-6"><Mail className="w-8 h-8 text-[#1D1D1F]" /></div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tighter text-[#1D1D1F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("contact_title")}</h1>
              <p className="text-[#86868B] text-base md:text-lg max-w-md mx-auto">{t("contact_subtitle")}</p>
            </div>
          </Reveal>
          <Reveal delay={150}>
          {sent ? (
            <div data-testid="contact-success" className="text-center animate-fade-in-up">
              <div className="w-20 h-20 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-[#34C759]" /></div>
              <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("contact_success_title")}</h2>
              <p className="text-[#86868B] mb-8">{t("contact_success_msg")}</p>
              <Button data-testid="contact-send-another" onClick={() => { setSent(false); setName(""); setEmail(""); setSubject(""); setMessage(""); }} variant="outline" className="rounded-full px-8 py-5 border-black/10">{t("contact_another")}</Button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={handleSubmit} className="bg-white rounded-3xl border border-black/5 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)] space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><Label className="text-xs font-semibold tracking-[0.1em] uppercase text-[#86868B] mb-2 block">{t("contact_name")} *</Label><Input data-testid="contact-name-input" placeholder={t("contact_name_placeholder")} value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl border-black/10" required /></div>
                <div><Label className="text-xs font-semibold tracking-[0.1em] uppercase text-[#86868B] mb-2 block">{t("contact_email")} *</Label><Input data-testid="contact-email-input" type="email" placeholder={t("contact_email_placeholder")} value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border-black/10" required /></div>
              </div>
              <div><Label className="text-xs font-semibold tracking-[0.1em] uppercase text-[#86868B] mb-2 block">{t("contact_subject")}</Label><Input data-testid="contact-subject-input" placeholder={t("contact_subject_placeholder")} value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl border-black/10" /></div>
              <div><Label className="text-xs font-semibold tracking-[0.1em] uppercase text-[#86868B] mb-2 block">{t("contact_message")} *</Label><Textarea data-testid="contact-message-input" placeholder={t("contact_message_placeholder")} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-xl border-black/10 min-h-[150px] resize-none" required /></div>
              <Button data-testid="contact-submit-button" type="submit" disabled={sending} className="w-full bg-black text-white rounded-full py-6 font-medium text-base hover:bg-black/90 transition-all btn-press disabled:opacity-40">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center gap-2"><Send className="w-4 h-4" />{t("contact_send")}</span>}
              </Button>
              <p className="text-xs text-center text-[#86868B]">{t("contact_direct")}{" "}<a href="mailto:office@sharedspace-rentals.com" className="text-[#1D1D1F] hover:underline">office@sharedspace-rentals.com</a></p>
            </form>
          )}
          </Reveal>
        </div>
      </div>
      <Footer />
    </div>
  );
}
